<template>
  <transition name="slide-in-modal">
    <!-- biome-ignore lint/a11y/noStaticElementInteractions: a click outside the panel closes it; Esc and the close button do the same by keyboard -->
    <!-- biome-ignore lint/a11y/useKeyWithClickEvents: Esc closes the panel (modalKeyDispatcher) -->
    <!-- biome-ignore lint/a11y/noNoninteractiveElementInteractions: see above -->
    <div
      class="slide-in-modal__base"
      v-show="modalOpen"
      @click.prevent="toggleModal"
    >
      <div class="slide-in-modal__overlay site-overlay" />

      <div class="slide-in-modal__actions">
        <close-button
          :class="'btn icon slide-in-modal__close-button'"
          :close-action="toggleModal"
          :label="closeLabel"
        />

        <back-button
          :back-toggle-function="backToggleFunction"
          :class="'btn icon slide-in-modal__back-button'"
        />
      </div>

      <!-- biome-ignore lint/a11y/useKeyWithClickEvents: only stops a click inside the panel from closing it -->
      <!-- biome-ignore lint/a11y/noNoninteractiveElementInteractions: see above -->
      <aside class="slide-in-modal__wrapper" ref="scrollLockTarget" @click.stop>
        <div class="slide-in-modal__box">
          <transition mode="out-in" name="modal-content">
            <header
              class="slide-in-modal__header"
              v-if="modalHeading"
              :key="modalHeading"
            >
              <div class="slide-in-modal__header-heading">
                <div class="slide-in-modal__header-icon" v-if="validIconName">
                  <img alt="" :src="headingIconSrc">
                </div>
                <svg
                  aria-hidden="true"
                  class="icon icon--2x"
                  v-else-if="modalHeadingIconName === 'check'"
                >
                  <use href="#circle-check" />
                </svg>
                <h2 :title="modalHeading" v-html="modalHeading" />
              </div>
              <p
                class="slide-in-modal__header-desc"
                v-if="modalHeaderDesc"
                v-html="modalHeaderDesc"
              />
            </header>
          </transition>

          <div class="slide-in-modal__box-wrapper" ref="modalContent">
            <div class="slide-in-modal__box-main">
              <slot />
            </div>
          </div>
        </div>
      </aside>
    </div>
  </transition>
</template>

<script>
  import {
    clearAllBodyScrollLocks,
    disableBodyScroll,
    enableBodyScroll,
  } from "body-scroll-lock";
  import BackButton from "./BackButton.vue";
  import CloseButton from "./CloseButton.vue";

  /** The header's line illustrations, by `modalHeadingIconName`. */
  const HEADING_ICONS = {
    gift: "gift.svg",
    media: "media.svg",
    osa: "osa.svg",
    pen: "pen.svg",
  };

  export default {
    beforeUnmount() {
      clearAllBodyScrollLocks(this.$refs.scrollLockTarget);
    },

    components: {
      BackButton,
      CloseButton,
    },

    computed: {
      headingIconSrc() {
        return `${theme_vars.assetsUrl}/images/illustrations/${HEADING_ICONS[this.modalHeadingIconName] ?? "osa.svg"}`;
      },
      validIconName() {
        return Object.hasOwn(HEADING_ICONS, this.modalHeadingIconName);
      },
    },

    methods: {
      modalKeyDispatcher(event) {
        if (this.modalOpen && event.keyCode === 27) {
          event.preventDefault();
          this.toggleModal();
        }
      },
    },
    name: "SlideInModal",

    props: {
      backToggleFunction: {
        default: null,
        type: Function,
      },
      backToggleLabel: {
        default: "",
        type: String,
      },
      // Shown beside the cross on phones, so it must be in the page's language.
      closeLabel: {
        default: "",
        type: String,
      },
      disabled: {
        required: true,
        type: Boolean,
      },
      modalHeaderDesc: {
        default: "",
        type: String,
      },
      modalHeading: {
        required: true,
        type: String,
      },
      modalHeadingIconName: {
        default: "",
        type: String,
      },
      modalOpen: {
        required: true,
        type: Boolean,
      },
      toggleModal: {
        required: true,
        type: Function,
      },
    },

    updated() {
      if (this.disabled) {
        return;
      }

      if (this.modalOpen) {
        disableBodyScroll(this.$refs.scrollLockTarget);
        document.body.classList.add("modal-open");
        document.addEventListener("keyup", this.modalKeyDispatcher);
      } else {
        enableBodyScroll(this.$refs.scrollLockTarget);
        document.body.classList.remove("modal-open");
        document.removeEventListener("keyup", this.modalKeyDispatcher);
      }
    },
  };
</script>
